// client/src/neetjee_guru/components/libraryManagement/Fines.jsx
import React, { Component } from "react";
import { Table, Button, Modal, Form, Badge, InputGroup, FormControl, Alert, Spinner, Pagination } from "react-bootstrap";
import * as Cookies from "es-cookie";
import { GET_LIBRARY_FINES } from "./queries";
import { RECORD_FINE_PAYMENT, WAIVE_FINE } from "./mutations";

class Fines extends Component {
  constructor(props) {
    super(props);
    this.state = {
      activeTab: "All", // "All", "Pending", "Partial", "Paid", "Waived"
      finesList: [],
      searchQuery: "",
      loading: true,
      error: null,
      
      // Pagination State
      currentPage: 1,
      pageSize: 10,
      
      // Modal errors
      modalError: null,
      modalSuccess: null,
      
      // Pay Modal
      showPayModal: false,
      selectedFine: null,
      payAmount: 0.0,
      
      // Waive Modal
      showWaiveModal: false,
      waiveReason: "",
      
      processing: false,
    };
  }

  componentDidMount() {
    this.fetchFines();
  }

  fetchFines = async () => {
    const { client } = this.props;
    const { activeTab } = this.state;
    const instId = parseInt(Cookies.get("institute_id") || Cookies.get("institutionid") || "0");
    const branchId = parseInt(Cookies.get("branch_id") || "0");

    try {
      this.setState({ loading: true });
      const { data } = await client.query({
        query: GET_LIBRARY_FINES,
        variables: {
          status: activeTab === "All" ? null : activeTab,
          institute_id: instId,
          branch_id: branchId,
        },
        fetchPolicy: "network-only",
      });
      this.setState({ finesList: data.getLibraryFines || [], loading: false, error: null, currentPage: 1 });
    } catch (err) {
      console.error("Error fetching fines:", err);
      this.setState({ error: err.message, loading: false });
    }
  };

  handlePageChange = (page) => {
    this.setState({ currentPage: page });
  };

  handlePageSizeChange = (e) => {
    const size = parseInt(e.target.value) || 10;
    this.setState({ pageSize: size, currentPage: 1 });
  };

  handleTabClick = (tab) => {
    this.setState({ activeTab: tab, currentPage: 1 }, this.fetchFines);
  };

  handleFieldChange = (e) => {
    const { name, value } = e.target;
    this.setState({ [name]: value, currentPage: 1 });
  };

  handleNumberChange = (e) => {
    const { name, value } = e.target;
    this.setState({ [name]: parseFloat(value) || 0.0 });
  };

  openPayModal = (fine) => {
    this.setState({
      showPayModal: true,
      modalError: null,
      modalSuccess: null,
      selectedFine: fine,
      payAmount: fine.balance_amount,
    });
  };

  openWaiveModal = (fine) => {
    this.setState({
      showWaiveModal: true,
      modalError: null,
      modalSuccess: null,
      selectedFine: fine,
      waiveReason: "",
    });
  };

  handlePaySubmit = async (e) => {
    e.preventDefault();
    const { selectedFine, payAmount } = this.state;
    if (!selectedFine) return;

    const { client } = this.props;
    const instId = parseInt(Cookies.get("institute_id") || Cookies.get("institutionid") || "0");
    const branchId = parseInt(Cookies.get("branch_id") || "0");

    try {
      this.setState({ processing: true, modalError: null });
      await client.mutate({
        mutation: RECORD_FINE_PAYMENT,
        variables: {
          fine_id: selectedFine.id,
          amount: parseFloat(payAmount),
          institute_id: instId,
          branch_id: branchId,
        },
      });
      this.setState({ showPayModal: false, selectedFine: null, processing: false });
      this.fetchFines();
      if (this.props.showToast) {
        this.props.showToast(`Fine payment of ₹${parseFloat(payAmount).toFixed(2)} recorded successfully!`, "success");
      }
    } catch (err) {
      this.setState({ modalError: "Error recording payment: " + err.message, processing: false });
    }
  };

  handleWaiveSubmit = async (e) => {
    e.preventDefault();
    const { selectedFine, waiveReason } = this.state;
    if (!selectedFine) return;

    const { client } = this.props;
    const instId = parseInt(Cookies.get("institute_id") || Cookies.get("institutionid") || "0");
    const branchId = parseInt(Cookies.get("branch_id") || "0");

    try {
      this.setState({ processing: true, modalError: null });
      await client.mutate({
        mutation: WAIVE_FINE,
        variables: {
          fine_id: selectedFine.id,
          reason: waiveReason,
          institute_id: instId,
          branch_id: branchId,
        },
      });
      this.setState({ showWaiveModal: false, selectedFine: null, processing: false });
      this.fetchFines();
      if (this.props.showToast) {
        this.props.showToast("Fine waived successfully!", "success");
      }
    } catch (err) {
      this.setState({ modalError: "Error waiving fine: " + err.message, processing: false });
    }
  };

  formatCurrency = (val) =>
    "₹" + parseFloat(val || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  getBadgeColor = (status) => {
    const s = status.toLowerCase();
    if (s === "paid") return "#dcfce7";
    if (s === "partially_paid" || s === "partial") return "#fef3c7";
    if (s === "waived") return "#f1f5f9";
    return "#fee2e2"; // unpaid / pending
  };

  getBadgeTextColor = (status) => {
    const s = status.toLowerCase();
    if (s === "paid") return "#15803d";
    if (s === "partially_paid" || s === "partial") return "#b45309";
    if (s === "waived") return "#475569";
    return "#ef4444";
  };

  render() {
    const {
      activeTab,
      finesList,
      searchQuery,
      loading,
      error,
      showPayModal,
      selectedFine,
      payAmount,
      showWaiveModal,
      waiveReason,
      processing,
      currentPage,
      pageSize,
      modalError,
    } = this.state;

    const tabs = ["All", "Pending", "Partial", "Paid", "Waived"];

    const filteredFines = finesList.filter((f) => {
      const q = searchQuery.toLowerCase();
      const matchName = f.borrower_name.toLowerCase().includes(q);
      const matchCode = f.borrower_code.toLowerCase().includes(q);
      const matchBook = f.book_title.toLowerCase().includes(q);
      const matchAccession = (f.accession_no || "").toLowerCase().includes(q);
      return matchName || matchCode || matchBook || matchAccession;
    });

    const totalCount = filteredFines.length;
    const totalPages = Math.ceil(totalCount / pageSize) || 1;
    const paginatedFines = filteredFines.slice((currentPage - 1) * pageSize, currentPage * pageSize);

    const pageItems = [];
    const startPage = Math.max(1, currentPage - 2);
    const endPage = Math.min(totalPages, currentPage + 2);

    for (let p = startPage; p <= endPage; p++) {
      pageItems.push(
        <Pagination.Item 
          key={p} 
          active={p === currentPage} 
          onClick={() => this.handlePageChange(p)}
        >
          {p}
        </Pagination.Item>
      );
    }

    return (
      <div>
        {/* Navigation Tabs */}
        <div className="mb-4">
          <ul className="d-flex list-unstyled gap-2 mb-0" style={{ gap: 12 }}>
            {tabs.map((tab) => {
              const tabVal = tab === "Pending" ? "unpaid" : (tab === "Partial" ? "partially_paid" : tab);
              const isActive = activeTab === tabVal || (tab === "All" && activeTab === "All");
              return (
                <li key={tab}>
                  <Button
                    onClick={() => this.handleTabClick(tabVal)}
                    style={{
                      background: isActive ? "#6366f1" : "#f1f5f9",
                      border: "none",
                      color: isActive ? "#fff" : "#4b5563",
                      fontWeight: 700,
                      fontSize: 13,
                      padding: "8px 16px",
                      borderRadius: 20,
                      cursor: "pointer",
                      boxShadow: isActive ? "0 2px 6px rgba(99,102,241,0.2)" : "none",
                    }}
                  >
                    {tab}
                  </Button>
                </li>
              );
            })}
          </ul>
        </div>

        {/* Search filter */}
        <div className="mb-4" style={{ maxWidth: 400 }}>
          <InputGroup>
            <InputGroup.Prepend>
              <InputGroup.Text style={{ background: "#fff", borderRight: "none", color: "#64748b" }}>
                <i className="fal fa-search" />
              </InputGroup.Text>
            </InputGroup.Prepend>
            <FormControl
              placeholder="Search Borrower Name, Code, Book, Accession No..."
              name="searchQuery"
              value={searchQuery}
              onChange={this.handleFieldChange}
              style={{ borderLeft: "none", fontSize: 13 }}
            />
          </InputGroup>
        </div>

        {/* Fines table */}
        {loading ? (
          <div className="shimmer" style={{ height: 250, borderRadius: 12 }} />
        ) : error ? (
          <div className="p-4 text-center text-danger">Error: {error}</div>
        ) : paginatedFines.length === 0 ? (
          <div className="text-center p-5 text-muted border rounded bg-white">
            <i className="fal fa-receipt fa-3x d-block mb-3 text-light" />
            <p>No fines found under this category.</p>
          </div>
        ) : (
          <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", overflow: "hidden" }}>
            <Table responsive hover className="mb-0" style={{ fontSize: 13 }}>
              <thead>
                <tr style={{ background: "#f8fafc", color: "#475569", borderBottom: "2px solid #e2e8f0" }}>
                  <th style={{ padding: "12px 20px" }}>Borrower Details</th>
                  <th>Book Details</th>
                  <th>Fine Reason</th>
                  <th className="text-center">Accrued Fine</th>
                  <th className="text-center">Paid Amount</th>
                  <th className="text-center">Balance Due</th>
                  <th className="text-center">Status</th>
                  <th className="text-center" style={{ width: 180 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedFines.map((fine) => (
                  <tr key={fine.id} style={{ borderBottom: "1px solid #f1f5f9" }}>
                    <td style={{ padding: "16px 20px" }}>
                      <div style={{ fontWeight: 800, color: "#1f2937" }}>{fine.borrower_name}</div>
                      <small style={{ color: "#64748b" }}>ID Code: {fine.borrower_code}</small>
                    </td>
                    <td>
                      <div style={{ fontWeight: 600 }}>{fine.book_title}</div>
                      <small className="text-muted">Accession: {fine.accession_no}</small>
                    </td>
                    <td>
                      <Badge variant="secondary" style={{ background: "#f1f5f9", color: "#475569", textTransform: "capitalize", padding: "4px 8px" }}>
                        {fine.fine_type.replace("_", " ")}
                      </Badge>
                      {fine.waive_reason && <small className="text-muted d-block mt-1">Waive Reason: {fine.waive_reason}</small>}
                    </td>
                    <td className="text-center" style={{ fontWeight: 600 }}>{this.formatCurrency(fine.fine_amount)}</td>
                    <td className="text-center" style={{ color: "#10b981", fontWeight: 600 }}>{this.formatCurrency(fine.paid_amount)}</td>
                    <td className="text-center" style={{ color: fine.balance_amount > 0 ? "#ef4444" : "#10b981", fontWeight: 700 }}>
                      {this.formatCurrency(fine.balance_amount)}
                    </td>
                    <td className="text-center">
                      <Badge
                        style={{
                          background: this.getBadgeColor(fine.status),
                          color: this.getBadgeTextColor(fine.status),
                          borderRadius: 20,
                          padding: "4px 10px",
                          fontWeight: 700,
                          textTransform: "capitalize"
                        }}
                      >
                        {fine.status.replace("_", " ")}
                      </Badge>
                    </td>
                    <td className="text-center">
                      {fine.balance_amount > 0 && fine.status.toLowerCase() !== "waived" ? (
                        <div className="d-flex justify-content-center gap-2" style={{ gap: 8 }}>
                          <Button
                            size="sm"
                            style={{ background: "#6366f1", border: "none", fontWeight: 700 }}
                            onClick={() => this.openPayModal(fine)}
                          >
                            Pay
                          </Button>
                          <Button
                            size="sm"
                            variant="light"
                            style={{ color: "#d97706", background: "#fef3c7", border: "none", fontWeight: 700 }}
                            onClick={() => this.openWaiveModal(fine)}
                          >
                            Waive
                          </Button>
                        </div>
                      ) : (
                        <span style={{ color: "#64748b", fontStyle: "italic" }}>Settled</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>

            {/* Pagination Controls Dock */}
            <div className="d-flex justify-content-between align-items-center px-4 py-3 bg-light border-top" style={{ borderBottomLeftRadius: 12, borderBottomRightRadius: 12 }}>
              <div className="text-muted" style={{ fontSize: 12 }}>
                Showing <strong>{totalCount === 0 ? 0 : (currentPage - 1) * pageSize + 1}</strong> to <strong>{Math.min(currentPage * pageSize, totalCount)}</strong> of <strong>{totalCount}</strong> entries
              </div>
              
              <div className="d-flex align-items-center" style={{ gap: 16 }}>
                <Form.Control 
                  as="select" 
                  size="sm" 
                  value={pageSize} 
                  onChange={this.handlePageSizeChange} 
                  style={{ width: 100, fontSize: 12, height: 32, borderRadius: 6, border: "1.5px solid #e2e8f0" }}
                >
                  <option value={10}>10 / page</option>
                  <option value={25}>25 / page</option>
                  <option value={50}>50 / page</option>
                  <option value={100}>100 / page</option>
                </Form.Control>

                <Pagination size="sm" className="mb-0">
                  <Pagination.First disabled={currentPage === 1} onClick={() => this.handlePageChange(1)} />
                  <Pagination.Prev disabled={currentPage === 1} onClick={() => this.handlePageChange(currentPage - 1)} />
                  {pageItems}
                  <Pagination.Next disabled={currentPage >= totalPages} onClick={() => this.handlePageChange(currentPage + 1)} />
                  <Pagination.Last disabled={currentPage >= totalPages} onClick={() => this.handlePageChange(totalPages)} />
                </Pagination>
              </div>
            </div>
          </div>
        )}

        {/* Record Payment Modal */}
        <Modal show={showPayModal} onHide={() => this.setState({ showPayModal: false, selectedFine: null })} centered>
          {selectedFine && (
            <Form onSubmit={this.handlePaySubmit}>
              <Modal.Header closeButton style={{ background: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
                <Modal.Title style={{ fontSize: 16, fontWeight: 800 }}>Record Fine Payment</Modal.Title>
              </Modal.Header>
              <Modal.Body style={{ padding: "24px" }}>
                {modalError && (
                  <Alert variant="danger" dismissible onClose={() => this.setState({ modalError: null })}>
                    {modalError}
                  </Alert>
                )}
                <div style={{ background: "#eff6ff", padding: "12px 16px", borderRadius: 8, marginBottom: 16 }}>
                  <small style={{ color: "#64748b", fontWeight: 700 }}>BORROWER</small>
                  <div style={{ fontWeight: 800, color: "#1f2937" }}>{selectedFine.borrower_name}</div>
                  <small className="text-muted">Total Accrued: {this.formatCurrency(selectedFine.fine_amount)} | Balance Due: {this.formatCurrency(selectedFine.balance_amount)}</small>
                </div>

                <Form.Group className="mb-3">
                  <Form.Label style={{ fontSize: 12, fontWeight: 700 }}>Payment Amount (₹) *</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    min="0.01"
                    max={selectedFine.balance_amount}
                    name="payAmount"
                    value={payAmount}
                    onChange={this.handleNumberChange}
                    required
                  />
                  <small className="text-muted">Enter fine amount received from the borrower.</small>
                </Form.Group>
              </Modal.Body>
              <Modal.Footer style={{ background: "#f8fafc", borderTop: "1px solid #e2e8f0" }}>
                <Button variant="light" onClick={() => this.setState({ showPayModal: false, selectedFine: null })} style={{ fontWeight: 600 }}>
                  Cancel
                </Button>
                <Button type="submit" style={{ background: "#6366f1", border: "none", fontWeight: 700 }} disabled={processing}>
                  {processing ? <Spinner animation="border" size="sm" /> : "Confirm Payment"}
                </Button>
              </Modal.Footer>
            </Form>
          )}
        </Modal>

        {/* Waive Fine Modal */}
        <Modal show={showWaiveModal} onHide={() => this.setState({ showWaiveModal: false, selectedFine: null })} centered>
          {selectedFine && (
            <Form onSubmit={this.handleWaiveSubmit}>
              <Modal.Header closeButton style={{ background: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
                <Modal.Title style={{ fontSize: 16, fontWeight: 800 }}>Waive Outstanding Fine</Modal.Title>
              </Modal.Header>
              <Modal.Body style={{ padding: "24px" }}>
                {modalError && (
                  <Alert variant="danger" dismissible onClose={() => this.setState({ modalError: null })}>
                    {modalError}
                  </Alert>
                )}
                <div style={{ background: "#fef3c7", padding: "12px 16px", borderRadius: 8, marginBottom: 16 }}>
                  <small style={{ color: "#d97706", fontWeight: 700 }}>WAIVING OUTSTANDING DUE FOR:</small>
                  <div style={{ fontWeight: 800, color: "#1f2937" }}>{selectedFine.borrower_name}</div>
                  <div style={{ fontWeight: 700, color: "#ef4444" }}>Outstanding: {this.formatCurrency(selectedFine.balance_amount)}</div>
                </div>

                <Form.Group className="mb-3">
                  <Form.Label style={{ fontSize: 12, fontWeight: 700 }}>Waiving Reason *</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="waiveReason"
                    value={waiveReason}
                    onChange={this.handleFieldChange}
                    placeholder="Enter reason for waiving this fine (e.g. Approved by Principal, Medical reasons)"
                    required
                  />
                </Form.Group>
              </Modal.Body>
              <Modal.Footer style={{ background: "#f8fafc", borderTop: "1px solid #e2e8f0" }}>
                <Button variant="light" onClick={() => this.setState({ showWaiveModal: false, selectedFine: null })} style={{ fontWeight: 600 }}>
                  Cancel
                </Button>
                <Button type="submit" style={{ background: "#d97706", border: "none", color: "#fff", fontWeight: 700 }} disabled={processing}>
                  {processing ? <Spinner animation="border" size="sm" /> : "Confirm Waive"}
                </Button>
              </Modal.Footer>
            </Form>
          )}
        </Modal>
      </div>
    );
  }
}

export default Fines;
